import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";


import FormSummary from "components/FormSummary";
import StatusDropdown from "components/FormSummary/StatusDropdown";
import SPLink from "components/SPLink";
import StatusCommentModal from "components/StatusCommentModal";
import {
	DEFAULT_SUBAWARD_COLUMN_HEADERS,
	COLUMN_HEADER_KEYS,
	IS_DEV,
	AWARDS_BASE_ROUTE,
	PROJECTS_BASE_ROUTE
} from "globals/constants";
import { useColumnHeaders,
		 useDictionary,
		 useResolutions,
		 useFormAccesses
} from "hooks";
import { getSubaward, getSubawardValues } from "store/actions/subaward";
import { tenantSelectors, formSelectors, subawardSelectors } from "store/selectors";
import { notifyError } from "utils";
import { useProtectedAction } from "../../../hooks/useProtectedAction";

import { getFormModifications } from "../../../store/actions/modifications";
import styles from "./styles.css";

const DEFAULT_ACTIVE_COLUMN_HEADERS = [
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARDEE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.TOTAL_EXPECTED,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_BEGIN_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_ADMIN_CONTACT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_END_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_CONTACT_EMAIL,
];

const link = ({ label, url, text, onClick }) => {
	return (
		<>
			<span className={styles.label}>{label}:</span>
			<SPLink to={url} aria-label={`Go to ${label}: ${text}`} onClick={onClick}>
				{text}
			</SPLink>
		</>
	);
};

const SubawardSummary = () => {
	const { awardId, subawardId } = useParams();
	const { subaward } = useSelector(subawardSelectors.content);
	const { isFetching: isSubawardValuesFetching, data } = useSelector(subawardSelectors.values) ?? {};
	const { savingIndicator: isFormSaving, isEditMode, isFileUploading } = useSelector(formSelectors.entity);
	const tenant = useSelector(tenantSelectors.tenant);
	const { subawardStatuses } = tenant;
	const projectLevelPermissionsEnabled = tenant?.projectLevelPermissionsEnabled ?? true;

	const formInstance = useMemo(() => {
		return {
			instance: subaward,
			instanceId: subaward.subawardId,
			values: data,
		};
	}, [ subaward, data ]);

	const [ resolutionSelected, setResolutionSelected ] = useState(null);
	const [ resolutionsVisible, setResolutionsVisible ] = useState(false);
	const [ statusOptions, setStatusOptions ] = useState([]);
	const [ statusOverrides, setStatusOverrides ] = useState([]);
	const [ statusOverrideId, setStatusOverrideId ] = useState(null);
	const [ isStatusLoading, setIsStatusLoading ] = useState(false);
	const [ statusSelected, setStatusSelected ] = useState(null);
	const [ commentModalVisible, setCommentModalVisible ] = useState(false);
	const [ commentModalTitle, setCommentModalTitle ] = useState("");
	const [ comment, setComment ] = useState("");

	const { defaultColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.SUBAWARD, {
		defaultColumnHeaders: DEFAULT_ACTIVE_COLUMN_HEADERS
	});
	const dictionary = useDictionary("awards.subawardDetails.summary");
	const resolutions = useResolutions(COLUMN_HEADER_KEYS.SUBAWARD);
	const formAccesses = useFormAccesses("subaward", subaward.subawardId);
	const awardFormAccesses = useFormAccesses("award", { checkParentAccess: true });
	const statusDropdownDisabled = !formAccesses.changeStatus || !isNonEmptyArray(statusOverrides) || isFormSaving || isEditMode || isFileUploading;
	const hasAccessToViewParentAward = awardFormAccesses.view || projectLevelPermissionsEnabled;

	const onCommentOpen = (fromStatus, toStatus, toStatusId) => {
		const isResolutionVisible = (toStatus === "Closed");

		setCommentModalTitle(`Change Subaward Status: ${fromStatus} to ${toStatus}`);
		setStatusOverrideId(toStatusId);
		setComment("");
		setResolutionSelected(null);
		setResolutionsVisible(isResolutionVisible);
		setCommentModalVisible(true);
		setStatusSelected(toStatus);
	};

	const onCommentClose = (actionType = "close") => {
		setCommentModalVisible(false);
		actionType !== "confirm" && setStatusSelected(subaward.statusName);
	};

	const onConfirm = async () => {
		setIsStatusLoading(true);

		try {
			await api.subaward.updateStatus(subaward.subawardId, {
				id: statusOverrideId,
				comment: comment,
				resolution: resolutionSelected
			});

			onCommentClose("confirm");
			getSubaward(subawardId);
			await loadSubAwardStatus(subaward.subawardId);
		} catch (error) {
			const message = IS_DEV ? error.message : dictionary.completeReview.errors.reviewNotCompleted;
			notifyError(message, error);
		} finally {
			setIsStatusLoading(false);
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			const formInfo = {
				formId: subawardId,
				isSubaward: true,
			};
			await getFormModifications({ formInfo });
			getSubaward(subawardId);
		};

		if (!subaward.subawardId) {
			fetchData();
			return
		}
	}, []);

	useEffect(() => {
		if (isNonEmptyString(subaward.subawardId) && !isSubawardValuesFetching) {
			getSubawardValues({ awardId: subaward.awardId, subawardId: subaward.subawardId });
		}
	}, [subaward?.subawardId ]);

	useEffect(() => {
		if (isNonEmptyString(subaward?.statusName)) {
			setStatusSelected(subaward.statusName);
		}
	}, [ subaward?.statusName ]);

	const loadSubAwardStatus = useCallback(async subawardId => {
		try {
			const data = await api.subaward.getStatus(subawardId);
			setStatusOverrides(data);
		} catch (error) {
			notifyError("Something went wrong.", error);
			setStatusOverrides([]);
		}
	}, []);

	useEffect(() => {
		if (isNonEmptyArray(subawardStatuses)) {
			const options = subawardStatuses.map(item => {
				const disabled = item.description === statusSelected;
				const toStatus = item.description;
				const toStatusId = item.id;

				return {
					className: styles.statusOverride,
					key: item.id,
					value: item.description,
					text: item.description,
					disabled: disabled,
					onClick: () => !disabled && onCommentOpen(statusSelected, toStatus, toStatusId)
				};
			});
			setStatusOptions(options);
		}
	}, [ subawardStatuses, statusOverrides, statusSelected ]);

	useEffect(() => {
		if (isNonEmptyString(subaward.subawardId)) {
			loadSubAwardStatus(subaward.subawardId);
		}
	}, [ subaward.subawardId, formAccesses.changeStatus ]);

	const resolutionOptions = useMemo(() => {
		if (!resolutions) {
			return [];
		}

		return resolutions
			.filter(resolution => resolution.active)
			.sort((a, b) => (a.value > b.value) ? 1 : -1)
			.map(item => {
				return {
					key: item.value,
					value: item.value,
					label: item.value
				};
			});
	}, [ resolutions ]);

	const handleAwardLinkClick = useProtectedAction({
		hasAccess: hasAccessToViewParentAward,
		errorMessage: dictionary.noAwardViewAccess
			|| "You do not have permission to view the parent award." +
			" Please contact an administrator to check your permissions."
	});

	const linkSection = useMemo( () => {
		const items = [];

		if(subaward.awardId) {
			const { awardId, awardNumber } = subaward;
			const url = hasAccessToViewParentAward
				? `${AWARDS_BASE_ROUTE}/${awardId}`
				: "#";

			items.push(link({ label: "Award", url: url, text: awardNumber, onClick: handleAwardLinkClick }));
		} else {
			const { projectId, projectNumber } = subaward;

			items.push(link({ label: "Project", url: `${PROJECTS_BASE_ROUTE}/${projectId}`, text: projectNumber }));
		}

		if(isNonEmptyArray(items)) {
			return (
				<ul className={styles.summaryLinkList}>
					{items.map((item, index) => item ? <li key={`summary-link-${index}`} className={styles.summaryRow}>{item}</li> : "")}
				</ul>
			);
		}
	}, [ subaward, handleAwardLinkClick, hasAccessToViewParentAward ]);

	return (
		<div data-component="subawardSummary">
			<StatusCommentModal
				comment={comment}
				title={commentModalTitle}
				resolutions={resolutionOptions}
				resolutionSelected={resolutionSelected}
				resolutionsVisible={resolutionsVisible}
				visible={commentModalVisible}
				onClose={onCommentClose}
				onConfirm={onConfirm}
				onCommentChange={value => setComment(value)}
				onResolutionChange={value => setResolutionSelected(value)}
				isLoading={isStatusLoading}
				formType={COLUMN_HEADER_KEYS.SUBAWARD}
			/>
			<FormSummary
				summaryTitle={ dictionary.title || "Subaward Summary" }
				statusDropdown={
					<StatusDropdown
						options={statusOptions}
						value={statusSelected}
						disabled={statusDropdownDisabled}
						ariaLabel="Select subaward status"
						label="Subaward status:"
					/>
				}
				defaultColumnHeaders={ defaultColumnHeaders }
				formInstance={ formInstance }
				formType={COLUMN_HEADER_KEYS.SUBAWARD}
				thirdColumnContent={linkSection}
			/>
		</div>
	);
};

export default SubawardSummary;
